import { verifyToken, checkRedeemCode, submitTask, getTaskStatus, retrySelfCard } from './api';
import { RETRY_CONFIG } from '../constants/config';
import { sleep } from '../utils';

const MAX_AUTO_RETRIES = 3;

export class TaskService {
  constructor(addLog) {
    this.addLog = addLog;
  }

  async validateToken(token, tokenType) {
    try {
      this.addLog(`正在验证令牌 (${tokenType})...`, 'info');
      
      const result = await verifyToken(token);
      
      if (result.success) {
        this.addLog(`令牌验证成功，用户邮箱: ${result.user_info.email}`, 'success');
        return { success: true, data: result };
      } else {
        this.addLog(`令牌验证失败: ${result.message}`, 'error');
        return { success: false, error: result.message };
      }
    } catch (error) {
      this.addLog(`令牌验证异常: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }

  async validateRedeemCode(code) {
    try {
      this.addLog(`正在验证兑换码: ${code}...`, 'info');
      
      const result = await checkRedeemCode(code);
      
      if (result.success) {
        this.addLog('兑换码验证成功', 'success');
        return {
          success: true,
          provider: result.provider,
          provider_display: result.provider_display,
          supports_overwrite: result.supports_overwrite || false,
        };
      } else {
        this.addLog(`兑换码验证失败: ${result.message}`, 'error');
        return { success: false, error: result.message };
      }
    } catch (error) {
      this.addLog(`兑换码验证异常: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }

  async submitTaskWithRetry(taskData, retryCount = 0) {
    const { MAX_RETRIES, RETRY_DELAY } = RETRY_CONFIG;
    
    try {
      this.addLog(`开始提交任务 (重试: ${retryCount}/${MAX_RETRIES})`, 'info');
      
      const result = await submitTask(taskData);
      
      if (result.success) {
        const channelInfo = result.channel ? ` (渠道 ${result.channel})` : '';
        this.addLog(`任务提交成功，任务ID: ${result.task_id}${channelInfo}`, 'success');

        if (result.channel_attempts?.length) {
          for (const attempt of result.channel_attempts) {
            if (attempt.status === 'failed' || attempt.status === 'skipped') {
              this.addLog(`渠道 ${attempt.channel}: ${attempt.message || attempt.status}`, 'info');
            }
          }
        }

        return {
          success: true,
          taskId: result.task_id,
          immediate: result.immediate || false,
          status: result.status,
          result: result.result,
          channel: result.channel,
          channelAttempts: result.channel_attempts,
        };
      } else {
        if (result.channel_attempts?.length) {
          for (const attempt of result.channel_attempts) {
            this.addLog(`渠道 ${attempt.channel}: ${attempt.message || attempt.status}`, 'error');
          }
        }
        throw new Error(result.message || '任务提交失败');
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        this.addLog(`任务提交失败: ${error.message}，准备重试...`, 'error');
        await sleep(RETRY_DELAY);
        return this.submitTaskWithRetry(taskData, retryCount + 1);
      } else {
        this.addLog(`任务提交失败（已达最大重试次数）: ${error.message}`, 'error');
        return { success: false, error: error.message };
      }
    }
  }

  async pollTaskStatus(taskId, onStatusChange, retryContext = null) {
    const { POLL_INTERVAL, MAX_POLL_RETRIES } = RETRY_CONFIG;
    let retries = 0;
    
    const checkStatus = async () => {
      try {
        retries++;
        this.addLog(`正在检查任务状态 (${retries}/${MAX_POLL_RETRIES})`, 'info');
        
        const result = await getTaskStatus(taskId);
        
        if (!result.success) {
          throw new Error(result.message || '获取任务状态失败');
        }
        
        const status = result.status;
        onStatusChange(status, result.result);
        
        if (status === 'completed') {
          this.addLog(`任务完成: ${result.result}`, 'success');
          return { success: true, result: result.result };
        } else if (status === 'failed') {
          if (result.can_retry && retryContext) {
            const excludeProviders = result.failed_providers || [];
            const autoRetryCount = retryContext.autoRetryCount || 0;

            if (autoRetryCount < MAX_AUTO_RETRIES) {
              this.addLog(
                `渠道 ${result.failed_provider} 轮询失败，卡密已自动回退，正在切换渠道重试 (${autoRetryCount + 1}/${MAX_AUTO_RETRIES})...`,
                'info'
              );
              onStatusChange('retrying', `正在切换渠道重试 (${autoRetryCount + 1}/${MAX_AUTO_RETRIES})...`);

              const retryResult = await this._doAutoRetry(retryContext, excludeProviders);
              if (retryResult.success) {
                const newCtx = {
                  ...retryContext,
                  autoRetryCount: autoRetryCount + 1,
                  excludeProviders,
                };

                if (retryResult.immediate) {
                  this.addLog(`重试成功（即时完成）: ${retryResult.result || '兑换成功'}`, 'success');
                  onStatusChange('completed', retryResult.result || '兑换成功');
                  return { success: true, result: retryResult.result || '兑换成功', retried: true };
                }

                onStatusChange('processing', null, retryResult.taskId);
                this.addLog(`重试已提交新任务 ${retryResult.taskId}，继续轮询...`, 'info');
                retries = 0;
                return this.pollTaskStatus(retryResult.taskId, onStatusChange, newCtx);
              } else {
                this.addLog(`自动重试失败: ${retryResult.error}`, 'error');
                onStatusChange('failed', retryResult.error);
                return { success: false, error: retryResult.error, canManualRetry: true };
              }
            }
          }
          this.addLog(`任务失败: ${result.result || '未知错误'}`, 'error');
          return { success: false, error: result.result };
        } else if ((status === 'processing' || status === 'pending') && retries < MAX_POLL_RETRIES) {
          if (result.expires_at && (Date.now() / 1000) < result.expires_at) {
            retries = Math.min(retries, Math.floor(MAX_POLL_RETRIES * 0.5));
            await sleep(15000);
          } else {
            await sleep(POLL_INTERVAL);
          }
          return checkStatus();
        } else {
          this.addLog('任务超时，请稍后手动检查', 'error');
          return { success: false, error: '任务超时' };
        }
      } catch (error) {
        this.addLog(`检查任务状态出错: ${error.message}`, 'error');
        return { success: false, error: error.message };
      }
    };
    
    return checkStatus();
  }

  async _doAutoRetry(retryContext, excludeProviders) {
    try {
      const result = await retrySelfCard({
        redeem_code: retryContext.redeemCode,
        email: retryContext.email,
        account_info: retryContext.accountInfo,
        access_token: retryContext.accessToken,
        user_id: retryContext.userId || '',
        original_input: retryContext.originalInput || '',
        exclude_providers: excludeProviders,
      });

      if (result.success) {
        const channelInfo = result.channel ? ` (渠道 ${result.channel})` : '';
        this.addLog(`重试提交成功${channelInfo}`, 'success');
        return {
          success: true,
          taskId: result.task_id,
          immediate: result.immediate || false,
          result: result.result,
          channel: result.channel,
        };
      } else {
        return { success: false, error: result.message || '暂无库存，请稍后再试' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}